// Central copy for the FieldInfo popover component. Keyed by a short slug that
// forms reference via <Field info="...">. Keep entries terse: a title, a plain
// -English description of what the field means/is used for, and a concrete example.
export type FieldHelpEntry = {
  title: string;
  description: string;
  example?: string;
};

export const fieldHelp: Record<string, FieldHelpEntry> = {
  amount: {
    title: "Amount",
    description: "What we bill the client for this line item.",
    example: "e.g. $4,500.00 for a month of managed services",
  },
  cost_basis: {
    title: "Cost basis",
    description:
      "What it costs us to deliver this line item — used to calculate margin (Amount minus Cost basis). Not shown to the client.",
    example: "e.g. $2,800.00 in contractor/cloud spend behind a $4,500 line",
  },
  job_code: {
    title: "Job code",
    description:
      "The client's billing/job code, used on invoices so the client can match charges to their own cost tracking. This is independent of any number embedded in the project name (e.g. a project named \"Super Bloom - 26025\" doesn't need its job code to be 26025) — they can and often do differ.",
    example: "e.g. ACME-1024",
  },
  external_id: {
    title: "External ref",
    description:
      "The ID of a linked ticket/issue in an external tool like Linear or Jira. Used to jump back to the source of work, not a billing code.",
    example: "e.g. ENG-482 or PROJ-1234",
  },
  po_number: {
    title: "PO number",
    description:
      "The client's purchase order number, only needed if the client requires one to process the invoice.",
    example: "e.g. PO-2026-0091",
  },
  beneficiary_client: {
    title: "Beneficiary client",
    description:
      "The client this expense/line item is actually for — i.e. who the work or spend benefits — regardless of who ends up being invoiced.",
    example: "e.g. work done for Acme, even if a parent org is invoiced",
  },
  invoice_to_client: {
    title: "Invoice to client",
    description:
      "Who actually receives and pays the invoice. Can differ from the beneficiary client, e.g. a parent/holding company that is billed on behalf of a subsidiary.",
    example: "e.g. Beneficiary = Acme Widgets, Invoice to = Acme Holdings",
  },
  funding_bucket: {
    title: "Funding bucket",
    description:
      "The pool of pre-committed/prepaid funds this line item draws down against, if any. Leave blank for standard pay-as-you-go billing.",
    example: "e.g. a $50k Google ad-credit grant being drawn down",
  },
  payer_method: {
    title: "Payer method",
    description:
      "How this line item gets paid — a direct invoice to the client, or drawn from a funding bucket/credit balance.",
    example: "e.g. \"Direct invoice\" vs \"Funding bucket\"",
  },
  client_hierarchy: {
    title: "Parent client",
    description:
      "Links this client under a parent org for roll-up reporting (e.g. a subsidiary under a holding company). Purely organizational — it does not change who gets invoiced.",
    example: "e.g. \"Acme Widgets\" under parent \"Acme Holdings\"",
  },
  expiration_date: {
    title: "Expiration date",
    description: "The date this funding bucket expires — funds can no longer be drawn down after this date.",
    example: "e.g. 2026-12-31",
  },
  expected_available_date: {
    title: "Expected available date",
    description: "The date these funds are expected to arrive/become available for use, if not yet received.",
    example: "e.g. 2026-08-15",
  },
  markup: {
    title: "Markup",
    description:
      "Additional margin added on top of the base amount, as either a flat amount or a percentage.",
    example: "e.g. 15% markup on a $1,000 pass-through cost",
  },
  cloud_total_amount: {
    title: "Total amount",
    description: "The full cloud bill for this provider/month before it's split across clients/projects.",
    example: "e.g. $12,340.55 for the AWS account this month",
  },
  cloud_source_link: {
    title: "Source link",
    description: "Link to the original billing export (S3/GCS/storage URI or BigQuery table) this allocation was derived from.",
    example: "e.g. a BigQuery table path or a signed export URL",
  },
  default_invoice_to: {
    title: "Default invoice to",
    description:
      "The client billed by default for this project's line items — used unless a specific line item overrides it.",
    example: "e.g. defaults new invoices to the parent company",
  },
  default_funding_bucket: {
    title: "Default funding bucket",
    description:
      "The funding bucket new line items on this project draw from by default, if any.",
    example: "e.g. auto-fills a prepaid credits bucket on new lines",
  },
  category: {
    title: "Category",
    description:
      "How this cloud cost line should be handled financially — billed straight to the client, absorbed internally as an expense, imported for records only, or excluded entirely.",
    example: "e.g. \"Bill to client\" vs \"Absorb internally as expense\"",
  },
  requires_po: {
    title: "Requires PO",
    description:
      "Whether this client requires a purchase order number on every invoice before it can be processed.",
    example: "e.g. enterprise clients with a procurement process",
  },
  requires_receipt: {
    title: "Requires receipt",
    description: "Whether reimbursable expenses for this client must include an attached receipt.",
    example: "e.g. required for client-billed travel/meals expenses",
  },
  cloud_billed: {
    title: "Cloud billed",
    description: "Whether this client is billed for cloud/infra costs at all (vs. absorbed by us).",
    example: "e.g. off for clients on a flat managed-services rate",
  },
  project_type: {
    title: "Project type",
    description: "Broad category of work this project represents, used for reporting/roll-ups.",
    example: "e.g. \"Support\" vs \"New build\"",
  },
  markup_type: {
    title: "Markup type",
    description:
      "How markup is applied to this expense — passed through at cost, a flat dollar amount, or a percentage.",
    example: "e.g. \"Pass-through\" vs \"Percentage\"",
  },
  markup_value: {
    title: "Markup value",
    description: "The flat amount or percentage to add on top of cost, depending on the markup type chosen.",
    example: "e.g. 15 for a 15% markup",
  },
  billing_section: {
    title: "Section",
    description: "Which billing category this line item belongs to on the invoice.",
    example: "e.g. \"Billable Hours\" vs \"Cloud Usage\"",
  },
  billable: {
    title: "Billable",
    description: "Whether this expense should be passed on to the client, or absorbed internally.",
    example: "e.g. off for an internal tooling purchase",
  },
  priority: {
    title: "Priority",
    description: "How urgent this task is relative to others — used for sorting/triage, not scheduling.",
    example: "e.g. \"High\" for a client-blocking issue",
  },
  bundle: {
    title: "Permission bundle",
    description:
      "A predefined set of column-level permissions applied to this role bundle in one step, instead of picking columns individually.",
    example: "e.g. \"Read-only\" grants view access with no edit rights",
  },
  source_account: {
    title: "Source account",
    description: "Which Google/cloud billing account this export was pulled from, for traceability.",
    example: "e.g. billing-acme@cordite.iam.gserviceaccount.com",
  },
  gcp_project_id: {
    title: "GCP project id",
    description:
      "The exact GCP project id as it appears in the billing export. Once mapped to a client here, future imports auto-resolve this project id without asking again.",
    example: "e.g. acme-prod-1",
  },
  parent_page: {
    title: "Parent page",
    description:
      "The note page this is a sub-page of. Only one level of nesting is allowed — a sub-page cannot itself have sub-pages.",
    example: "e.g. \"Onboarding\" nested under \"Runbooks\"",
  },
};
